import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { Plan } from '../../shared/classes/plan';
import { Utilities } from '../../shared/utilities';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { PlanService } from '../../shared/services/plan.service';
import { UserService } from '../../shared/services/user.service';
import { GeneralService } from '../../shared/services/general.service';

@Component({
  templateUrl: './summary-and-subscription-confirmation.component.html',
  styleUrls: ['./summary-and-subscription-confirmation.component.css']
})

export class SummaryAndSubscriptionConfirmationComponent implements OnInit, OnDestroy {

  public loading = true;
  public plans: Plan[] = [];
  public plan: Plan = null;
  public error = '';
  public message = '';
  public alert = '';
  public processing = false;
  public planId = null;
  public isBackendCallback = false;
  public meanOfPaymentData = null;
  public fingerprintData = {
    IdComercio: null,
    IdEnvironment: null,
    IdInvocacion: null
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private planService: PlanService,
    private userService: UserService,
    private generalService: GeneralService
  ) {
    const that = this;
    that.route.queryParams.subscribe(params => {
      that.planId = params.planId ? params.planId : null;
      that.isBackendCallback = (!!params.P || !!params.p) && (params.P === '1' || params.p === '1');
      that.loadData();
    });
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    const that = this;
    // Si al irme de la pantalla existe un script generado para el fingerprint, lo elimino.
    that.deleteFingerprint();
  }

  public goTo(url): void {
    this.router.navigate([url]);
  }

  public userIsLoggedIn(): boolean {
    return this.authenticationService.userIsLoggedIn();
  }

  loadData(successMessage?: string): void {
    const that = this;
    that.loading = true;
    let errorFound = false;
    that.plans = [];
    forkJoin([
      that.planService.getActivePlans(),
      that.userService.getUserSubscriptions(this.userIsLoggedIn()),
      that.userService.getAssociatedMeanOfPayment(),
      that.generalService.getFingerprintData()
      // tslint:disable-next-line:max-line-length
    ]).subscribe(([getActivePlansResponse, getUserSubscriptionsResponse, getAssociatedMeanOfPaymentResponse, getFingerprintDataResponse]) => {
        if (getActivePlansResponse) {
          if (!getActivePlansResponse.HasError && getActivePlansResponse.Planes) {
            getActivePlansResponse.Planes.forEach(onePlan => that.plans.push(Plan.parse(onePlan)));
          } else {
            errorFound = true;
            if (getActivePlansResponse.ErrorMessage){
              that.error = `<p><b>¡Error!</b></p><p>${getActivePlansResponse.ErrorMessage}</p>`;
            } else {
              that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
            }
          }
        } else {
          errorFound = true;
          that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
        }
        if (getUserSubscriptionsResponse) {
          if (!getUserSubscriptionsResponse.HasError && getUserSubscriptionsResponse.Suscripciones) {
            // Por cada Id de plan que tiene el usuario, busco el plan y le seteo la property LoggedInUserHasPlan en true
            getUserSubscriptionsResponse.Suscripciones.forEach(
              oneUserSubscription => {
                const plan = that.plans.find(
                  onePlan => onePlan.PlanId === oneUserSubscription.SuscripcionPlanId && oneUserSubscription.SuscripcionEstado === 'ACTIVA'
                );
                if (plan) {
                  plan.LoggedInUserHasPlan = true;
                }
              }
            );
          } else {
            errorFound = true;
            if (getUserSubscriptionsResponse.ErrorMessage){
              that.error = `<p><b>¡Error!</b></p><p>${getUserSubscriptionsResponse.ErrorMessage}</p>`;
            } else {
              that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
            }
          }
        } else {
          errorFound = true;
          that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
        }

        if (getUserSubscriptionsResponse) {
          console.log(getAssociatedMeanOfPaymentResponse);
          that.meanOfPaymentData = getAssociatedMeanOfPaymentResponse.MedioPagoData;
          if (that.meanOfPaymentData && that.meanOfPaymentData.UsuarioMedioPagoPendiente) {
            that.alert = '<p><b>Medio de pago pendiente de aprobación.</b></p><p>Por favor, intente realizar la suscripción más tarde.</p>';
          } else if (that.isBackendCallback && that.meanOfPaymentData && that.meanOfPaymentData.UsuarioMedioPagoActivo) {
            successMessage = '<p>¡Su medio de pago ha sido actualizado!</p>';
          } else if (
            that.isBackendCallback &&
            that.meanOfPaymentData &&
            !that.meanOfPaymentData.UsuarioMedioPagoActivo &&
            !that.meanOfPaymentData.UsuarioMedioPagoPendiente
          ) {
            that.error = '<p>¡Ha ocurrido un error al actualizar si medio  de pago!</p><p>Por favor, intente nuevamente.</p>';
          }
        } else {
          errorFound = true;
          that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
        }

        if (getFingerprintDataResponse) {
          that.fingerprintData.IdComercio = getFingerprintDataResponse.IdComercio;
          that.fingerprintData.IdEnvironment = getFingerprintDataResponse.IdEnvironment;
          that.fingerprintData.IdInvocacion = getFingerprintDataResponse.IdInvocacion;
          that.addFingerprint();
        } else {
          errorFound = true;
          that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
        }

        if (!errorFound) {
          that.plan = that.plans.find(onePlan => onePlan.PlanId === that.planId);

          if (!that.plan) {
            errorFound = true;
            that.error = '<p>No se ha encontrado información sobre el plan que desea contratar.</p>';
          } else if (that.plan.LoggedInUserHasPlan) {
            errorFound = true;
            that.error = '<p>Usted ya posee una suscripción para el plan.</p>';
          }
          if (successMessage) {
            that.message = successMessage;
          }
        }

        that.loading = false;
      },
      (error) => {
        Utilities.logError('Error al cargar los planes activos');
        Utilities.logError(error);
        that.loading = false;
        that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
      });
  }

  private addFingerprint(): void {
    const that = this;
    if (
      that.fingerprintData &&
      that.fingerprintData.IdEnvironment &&
      that.fingerprintData.IdComercio &&
      that.fingerprintData.IdInvocacion
    ) {
      // Si antes de agregar el script del fingerprint ya existía uno, lo elimino.
      that.deleteFingerprint();
      // Genero el script para el fingerprint
      const scriptSRC = `https://h.online-metrix.net/fp/tags.js?org_id=${that.fingerprintData.IdEnvironment}&session_id=${that.fingerprintData.IdComercio}${that.fingerprintData.IdInvocacion}`;
      const scriptTag = document.createElement('script');
      scriptTag.id = 'fingerprintScript';
      scriptTag.type = 'text/javascript';
      scriptTag.src = scriptSRC;
      // Agrego el script necesario para el fingerprint
      document.getElementsByTagName('head')[0].appendChild(scriptTag);
    }
  }

  private deleteFingerprint(): void {
    const existingScriptTag = document.getElementById('fingerprintScript');
    if (existingScriptTag) {
      document.getElementsByTagName('head')[0].removeChild(existingScriptTag);
    }
    /*const existingIframes = document.getElementsByTagName('iframe');
    if (existingIframes && existingIframes.length > 0) {
      document.getElementsByTagName('body')[0].removeChild(existingIframes[0]);
    }*/
  }

  public refreshPage(successMessage?: string): void {
    this.loadData(successMessage);
  }

  // changeMeanOfPayment
  public changeMeanOfPayment(planId: string): void {
    const that = this;
    const fingerprint = that.fingerprintData.IdInvocacion;
    if (!that.processing) {
      that.error = '';
      that.message = '';
      that.alert = '';
      that.processing = true;
      forkJoin([
        that.userService.changeMeanOfPayment(planId, fingerprint)
      ]).subscribe(([changeMeanOfPaymentResponse]) => {
          if (changeMeanOfPaymentResponse) {
            if (!changeMeanOfPaymentResponse.HasError) {
              // Si en la respuesta no viene error y viene UrlToPlexo, mando al usuario a esa pantalla.
              if (changeMeanOfPaymentResponse.UrlToPlexo && changeMeanOfPaymentResponse.UrlToPlexo.length > 0) {
                window.location.href = changeMeanOfPaymentResponse.UrlToPlexo;
              }/* else {
                that.processing = false;
                that.refreshPage('Medio de Pago Actualizado!');
              }*/
            } else {
              if (changeMeanOfPaymentResponse.ErrorMessage){
                that.error = `<p><b>¡Error!</b></p><p>${changeMeanOfPaymentResponse.ErrorMessage}</p>`;
              } else {
                that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
              }
            }
          } else {
            that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
          }
          that.processing = false;
        },
        (error) => {
          Utilities.logError('Error al cambiar el medio de pago');
          Utilities.logError(error);
          that.processing = false;
          localStorage.clear();
          that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
        });

    }
  }

  public subscribeToPlan(planId: string): void {
    const that = this;
    const fingerprint = that.fingerprintData.IdInvocacion;
    if (!that.processing) {
      that.error = '';
      that.message = '';
      that.alert = '';
      that.processing = true;
      forkJoin([
        that.userService.subscribeToPlan(planId, fingerprint)
      ]).subscribe(([subscribeToPlanResponse]) => {
          if (subscribeToPlanResponse) {
            if (!subscribeToPlanResponse.HasError) {
              // Si en la respuesta no viene error y viene UrlToPlexo, mando al usuario a esa pantalla.
              if (subscribeToPlanResponse.UrlToPlexo && subscribeToPlanResponse.UrlToPlexo.length > 0) {
                window.location.href = subscribeToPlanResponse.UrlToPlexo;
              } else {
                const navigationExtras: NavigationExtras = {
                  queryParams: {planId}
                };
                that.router.navigate(['resultado-de-suscripcion'], navigationExtras);
              }
            } else {
              if (subscribeToPlanResponse.ErrorMessage){
                that.error = `<p><b>¡Error!</b></p><p>${subscribeToPlanResponse.ErrorMessage}</p>`;
              } else {
                that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
              }
            }
          } else {
            that.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
          }
          that.processing = false;
        },
        (error) => {
          Utilities.logError('Error al suscribirse a un plan');
          Utilities.logError(error);
          that.processing = false;
          localStorage.clear();
          that.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
        });
    }
  }
}
